import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Observes the DOM for open dialog elements and toggles a 'dialog-open' class on the body.
 * This is necessary because the dialog 'open' attribute mutation doesn't trigger a standard event
 * that bubbles up to the window in a way that allows easy global CSS targeting without this class.
 */
export const useDialogObserver = () => {
    const location = useLocation()

    useEffect(() => {
        const checkDialogs = () => {
            const hasOpenDialog = document.querySelector('dialog[open]') !== null
            if (hasOpenDialog) {
                document.body.classList.add('dialog-open')
            } else {
                document.body.classList.remove('dialog-open')
            }
        }

        // Check initially
        checkDialogs()

        const observer = new MutationObserver((mutations) => {
            let shouldCheck = false
            for (const mutation of mutations) {
                if (mutation.type === 'childList') {
                    // Dialog added or removed
                    shouldCheck = true
                } else if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
                    // Open attribute changed
                    shouldCheck = true
                }
            }
            if (shouldCheck) checkDialogs()
        })

        // Observe the body for added/removed dialogs and attribute changes on the subtree
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['open'] // Only listen for open attribute changes
        })

        return () => {
            observer.disconnect()
            document.body.classList.remove('dialog-open')
        }
    }, [])

    // Re-check when location changes (navigation might close dialogs or change page structure)
    useEffect(() => {
        const hasOpenDialog = document.querySelector('dialog[open]') !== null
        if (!hasOpenDialog) {
            document.body.classList.remove('dialog-open')
        }
    }, [location])
}
